import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useSearchMessages } from '../hooks';

interface MessageSearchProps {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
  onResultSelect: (messageId: string) => void;
}

interface HighlightTextProps {
  text: string;
  query: string;
}

function HighlightText({ text, query }: HighlightTextProps) {
  if (!query.trim()) {
    return <span>{text}</span>;
  }

  const parts = text.split(new RegExp(`(${query})`, 'gi'));

  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-slate-900 px-1 rounded">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

export function MessageSearch({ conversationId, isOpen, onClose, onResultSelect }: MessageSearchProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchMessages = useSearchMessages(conversationId);
  const messages = searchMessages.data?.messages || [];

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (searchQuery.trim()) {
      searchMessages.mutate({ q: searchQuery });
    }
  }, [searchQuery, conversationId]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [messages.length]);

  useEffect(() => {
    if (messages.length > 0 && messages[currentIndex]) {
      onResultSelect(messages[currentIndex].id);
    }
  }, [currentIndex, messages, onResultSelect]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < messages.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setCurrentIndex(0);
    inputRef.current?.focus();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white/40 border-b border-white/20 p-3 shadow-sm glass-surface"
        >
          <div className="flex items-center gap-2">
            {/* Search Icon */}
            <div className="flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-neutral-800">
                <circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="2"/>
                <path d="M13 13L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Search Input */}
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('messaging.searchMessages')}
                className="w-full px-3 py-2 bg-white/80 border border-white/20 rounded-xl font-medium text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
              {searchQuery && (
                <button
                  onClick={handleClear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                  aria-label={t('messaging.clearSearch')}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-neutral-800">
                    <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Results Count */}
            {searchQuery && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm font-medium text-neutral-800">
                  {messages.length > 0 ? `${currentIndex + 1} ${t('messaging.of')} ${messages.length}` : t('messaging.noResults')}
                </span>

                {/* Navigation Buttons */}
                {messages.length > 0 && (
                  <div className="flex gap-1">
                    <button
                      onClick={handlePrevious}
                      disabled={currentIndex === 0}
                      className="p-1 bg-white/80 border border-white/20 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label={t('messaging.previousResult')}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="rotate-90">
                        <path d="M12 6L8 10L4 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={currentIndex === messages.length - 1}
                      className="p-1 bg-white/80 border border-white/20 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label={t('messaging.nextResult')}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="-rotate-90">
                        <path d="M12 6L8 10L4 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 bg-slate-800 text-white border border-white/20 rounded-lg hover:bg-slate-700 hover:border-slate-700 transition-colors font-medium"
              aria-label={t('messaging.closeSearch')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Results Preview */}
          {searchQuery && messages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-3 bg-white/80 border border-white/20 rounded-xl"
            >
              <div className="text-sm">
                <div className="font-medium text-neutral-800 mb-1">
                  {messages[currentIndex].sender.name}
                </div>
                <div className="text-neutral-800/80">
                  <HighlightText text={messages[currentIndex].content} query={searchQuery} />
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {new Date(messages[currentIndex].createdAt).toLocaleString()}
                </div>
              </div>
            </motion.div>
          )}

          {/* Loading State */}
          {searchMessages.isPending && (
            <div className="mt-3 text-center text-sm font-medium text-neutral-800">
              {t('messaging.searching')}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
